import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchIssuesSuccess,
    showIssueMessage,
    onSaveIssueSuccess,
    onDeleteIssueSuccess,
    addFavouriteIssueSuccess,
    onFetchSelectFeatureDataSuccess,
} from 'actions/issue';
import {
    ON_SAVE_ISSUE,
    FETCH_ALL_ISSUE,
    ON_DELETE_ISSUE,
    ADD_FAVOURITE_ISSUE,
    ON_FETCH_SELECT_FEATURE_DATA,
    ON_ISSUE_FEATURE_UPDATE,
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, updateDataRequest, deleteDataRequest  } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { ORGID_PATH_PARAM } from 'constants/api';
import { mapDataToSelectionData } from 'services/selectors';
import { getMockDrillDownData } from 'services/issueService';

const currentOrg = (state) => state.orgs.selectedOrg;
const getIssueList = (state) => state.issues.allIssue;
const getSelectedIssue = (state) => state.issues.selectedIssue;
const getSelectedFeatures = (state) => state.issues.featureSelectionList;

export function* fetchIssues() {
    try {
        const selectedOrg = yield select(currentOrg);
        const fetchedIssues = yield call(
            fetchDataRequest,
            'issues',
            [ORGID_PATH_PARAM],
            [selectedOrg ?.id]
        ) || [];
        yield put(fetchIssuesSuccess(fetchedIssues));
    } catch (error) {
        yield put(showIssueMessage(apiErrorMessageFormatter(error)));
    }
}

export function* deleteIssue({ payload }) {
    try {
        // payload should be the issueId
        const selectedOrg = yield select(currentOrg);
        yield call(
            deleteDataRequest,
            'deleteIssue',
            'Deleting issue '.concat(payload),
            [ORGID_PATH_PARAM, 'issueId'],
            [selectedOrg ?.id, payload]
        ) || [];
        yield put(onDeleteIssueSuccess(payload)); // just dispatch the id as the reducer will remove from the item from the list
    } catch (error) {
        yield put(showIssueMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveIssue({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allIssues = yield select(getIssueList);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload ?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg ?.id };
        }
        const savedIssue = yield call(
            addOrUpdateDataRequest,
            allIssues,
            finalPayload,
            'addIssue',
            'updateIssue',
            [ORGID_PATH_PARAM],
            [selectedOrg.id]);
        yield put(onSaveIssueSuccess(savedIssue));
    } catch (error) {
        yield put(showIssueMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0 
 */
export function* addFavouriteIssue({ payload }) {
    try {
        const allIssues = yield select(getIssueList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allIssues.filter((entry) => entry ?.id === payload ?.id);

        if (filtered ?.length > 0) {
            const entry = filtered[0];
            const updatedIssue = {
                ...entry,
                starred: !payload.starred
            };
            const auditMsg = 'Updating starred setting to '.concat(updatedIssue ?.starred).concat(' for issue ').concat(updatedIssue ?.id);
            yield call(
                updateDataRequest,
                updatedIssue,
                'updateIssue',
                auditMsg,
                [ORGID_PATH_PARAM],
                [selectedOrg.id],
            );
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteIssueSuccess(payload));
        }
    } catch (error) {
        yield put(showIssueMessage(error.messages));
    }
}

export function* onFetchSelectFeatureData() {
    const selectedIssue = yield select(getSelectedIssue);
    // TODO const allFeatures = yield select(getFeatureList);
    const allFeatures = getMockDrillDownData();
    const selectedDrillDownItems = (Array.isArray(selectedIssue?.features)) ? selectedIssue?.features : [];
    const selections = mapDataToSelectionData(allFeatures, selectedDrillDownItems) || null;
    yield put(onFetchSelectFeatureDataSuccess(selections));
}

export function* onIssueFeatureUpdate() {
    try {
        // go through each  - check if entry is being added or removed and cleanup the other object being referenced
        const selectedOrg = yield select(currentOrg);
        const selectedIssue = yield select(getSelectedIssue);
        const selectedFeatures = yield select(getSelectedFeatures);
        const updatedDrillDownSelections = new Array();
        selectedFeatures.forEach((selection) => {
            if (selection?.selected) {
                updatedDrillDownSelections.push(selection?.id);
            }
        });
        const updatedSelectedIssue = {
            ...selectedIssue,
            features: updatedDrillDownSelections
        };
        const auditMsg = 'Updating selected feature to '.concat(' for issue ').concat(updatedSelectedIssue ?.id);
        yield call(
            updateDataRequest,
            updatedSelectedIssue,
            'updateIssue',
            auditMsg,
            [ORGID_PATH_PARAM],
            [selectedOrg.id],
        );
        yield put(onSaveIssueSuccess(updatedSelectedIssue));
    } catch (error) {
        yield put(showIssueMessage(apiErrorMessageFormatter(error)));
    }
}

export function* fetchIssuesSaga() {
    yield takeEvery(FETCH_ALL_ISSUE, fetchIssues);
}

export function* saveIssueSaga() {
    yield takeEvery(ON_SAVE_ISSUE, saveIssue);
}

export function* deleteIssueSaga() {
    yield takeEvery(ON_DELETE_ISSUE, deleteIssue);
}

export function* addFavouriteIssueSaga() {
    yield takeEvery(ADD_FAVOURITE_ISSUE, addFavouriteIssue);
}

export function* onFetchSelectFeatureDataSaga() {
    yield takeEvery(ON_FETCH_SELECT_FEATURE_DATA, onFetchSelectFeatureData);
}

export function* onIssueFeatureUpdateSaga() {
    yield takeEvery(ON_ISSUE_FEATURE_UPDATE, onIssueFeatureUpdate);
}

export default function* rootSaga() {
    yield all([
        fork(fetchIssuesSaga),
        fork(saveIssueSaga),
        fork(deleteIssueSaga),
        fork(addFavouriteIssueSaga),
        fork(onFetchSelectFeatureDataSaga),
        fork(onIssueFeatureUpdateSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/issue.js